import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Story', href: '#story' },
  { label: 'Showcase', href: '#app-showcase' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Privacy', href: '#privacy' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Waitlist', href: '#waitlist' },
];

// NEW: إعدادات حركة موحدة تعتمد على اتجاه السكرول (فوق أو تحت)
const slideVariants = {
  initial: (dir: number) => ({
    y: dir * 20,
    opacity: 0,
  }),
  animate: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
  },
  exit: (dir: number) => ({
    y: dir * -20,
    opacity: 0,
    transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const [activeSection, setActiveSection] = useState('Overview');
  const [direction, setDirection] = useState(1);

  // NEW: استخدام ref لتفادي مشاكل الـ Stale Closures جوه الـ IntersectionObserver
  const activeSectionRef = useRef('Overview');

  // NEW: دالة مسؤولة عن تحديث القسم وحساب اتجاه الأنيميشن (صعود أو هبوط)
  const updateSectionWithDirection = (newLabel: string) => {
    if (newLabel === activeSectionRef.current) return;

    const oldIdx = NAV_LINKS.findIndex((l) => l.label === activeSectionRef.current);
    const newIdx = NAV_LINKS.findIndex((l) => l.label === newLabel);

    let dir = 1;
    if (oldIdx !== -1 && newIdx !== -1) {
      dir = newIdx > oldIdx ? 1 : -1; // لو نازلين لتحت dir=1، لو طالعين لفوق dir=-1
    } else if (newLabel === 'Overview') {
      dir = -1; // العودة لأعلى الصفحة
    } else if (activeSectionRef.current === 'Overview') {
      dir = 1; // النزول من أعلى الصفحة
    }

    activeSectionRef.current = newLabel;
    setDirection(dir);
    setActiveSection(newLabel);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowNavbar(window.scrollY > window.innerHeight * 0.6); // Show after scrolling down roughly to the Story section
    };

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const match = NAV_LINKS.find((link) => link.href === `#${entry.target.id}`);
          if (match) {
            updateSectionWithDirection(match.label);
          } else if (entry.target.id === '' || window.scrollY < 200) {
            updateSectionWithDirection('Overview');
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    NAV_LINKS.forEach((link) => {
      const el = document.querySelector(link.href);
      if (el) observer.observe(el);
    });

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const handleNavClick = (href: string, label: string) => {
    setMobileOpen(false);
    updateSectionWithDirection(label);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // MODIFIED: فلترة العناصر عشان القسم النشط يختفي من الناف بار ويبدل مكانه مع الـ Badge
  const centerNavLinks = NAV_LINKS.filter(
    (link) => link.label !== 'Waitlist' && link.label !== activeSection
  );

  return (
    <>
      <motion.header
        initial={{ y: -50, opacity: 0 }}
        animate={showNavbar ? { y: 0, opacity: 1 } : { y: -50, opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none"
      >
        <div
          className={`pointer-events-auto flex items-center justify-between gap-6 px-5 py-2.5 rounded-full transition-all duration-500 ${scrolled
              ? 'bg-[#0a1628]/80 backdrop-blur-xl border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.5)] py-2'
              : 'bg-white/5 backdrop-blur-md border border-white/10'
            }`}
        >
          {/* Left: Logo + Dynamic Active Section Indicator */}
          <div className="flex items-center gap-3">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                updateSectionWithDirection('Overview');
              }}
              className="flex items-center gap-2.5 shrink-0 cursor-pointer group"
            >
              <img
                src="/name.png"
                alt="PingAfter Logo"
                className="h-6 sm:h-7 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </a>

            {/* MODIFIED: شارة الحالة النشطة بتطبق اتجاه الأنيميشن الديناميكي */}
            <div className="hidden sm:flex items-center pl-3 border-l border-white/15 overflow-hidden h-7 min-w-[90px] justify-center">
              <AnimatePresence mode="popLayout" custom={direction}>
                <motion.span
                  key={activeSection}
                  custom={direction}
                  variants={slideVariants as any}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-[0_0_15px_rgba(52,211,153,0.2)] block whitespace-nowrap"
                >
                  {activeSection}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>

          {/* Center: Interactive Nav Links with Dynamic Slot Swapping */}
          <nav className="hidden lg:flex items-center gap-1 bg-white/5 px-3 py-1 rounded-full border border-white/5 overflow-hidden min-h-[36px]">
            <AnimatePresence mode="popLayout" custom={direction}>
              {centerNavLinks.map((link) => (
                <motion.button
                  key={link.label}
                  layout
                  custom={-direction}
                  variants={slideVariants as any}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  onClick={() => handleNavClick(link.href, link.label)}
                  className="relative px-3.5 py-1.5 rounded-full text-xs font-semibold text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-200 shrink-0 block"
                >
                  {link.label}
                </motion.button>
              ))}
            </AnimatePresence>
          </nav>

          {/* Right: Action Button & Mobile Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleNavClick('#waitlist', 'Waitlist')}
              className="relative group overflow-hidden rounded-full p-px font-semibold text-xs"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full animate-pulse-slow" />
              <div className="relative px-4 py-2 rounded-full bg-[#0a1628] transition-colors group-hover:bg-transparent flex items-center gap-1.5 text-white">
                <span>Early Access</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform text-emerald-400 group-hover:text-white" />
              </div>
            </button>

            <button
              className="lg:hidden p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors border border-transparent hover:border-white/10"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle Menu"
            >
              {mobileOpen ? <X className="w-5 h-5 text-emerald-400" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(20px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0a1628]/95 lg:hidden flex flex-col justify-center px-8"
          >
            <div className="absolute top-7 left-8 flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-emerald-400 font-semibold bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                Navigation
              </span>
            </div>

            <nav className="flex flex-col gap-6 my-auto">
              {NAV_LINKS.filter((link) => link.label !== 'Waitlist').map((link, idx) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 + 0.1 }}
                  onClick={() => handleNavClick(link.href, link.label)}
                  className="text-left group flex items-center justify-between py-2 border-b border-white/10"
                >
                  <span
                    className={`text-2xl font-bold transition-colors ${activeSection === link.label ? 'text-emerald-400' : 'text-white/80 group-hover:text-white'
                      }`}
                  >
                    {link.label}
                  </span>
                  <ArrowRight
                    className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${activeSection === link.label
                        ? 'text-emerald-400 opacity-100'
                        : 'text-white/20 opacity-0 group-hover:opacity-100'
                      }`}
                  />
                </motion.button>
              ))}
            </nav>

            <div className="pb-12">
              <button
                onClick={() => handleNavClick('#waitlist', 'Waitlist')}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-center shadow-[0_0_30px_rgba(52,211,153,0.3)] flex items-center justify-center gap-2"
              >
                <span>Join the Waitlist Now</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}